import { useTranslation } from "@coworker/locales";
import { ApplyFilter } from "@coworker/apprestructured/src/shared/filters/types/apply";
import {
  FilterType,
  FilterValue,
  InputType,
  SelectFilter,
} from "@coworker/apprestructured/src/shared/filters/types/filter";
import { getSelectedFilterValue } from "@coworker/apprestructured/src/shared/filters/utils/filter/filtervalue";
import { Group } from "../types/groups";

const SORT_ID = "sort";

function useSortSelectFilter(filterValues: FilterValue[]): SelectFilter {
  const { t } = useTranslation();
  const defaultValue = {
    filterId: SORT_ID,
    value: "byName",
  };
  const selected = getSelectedFilterValue(SORT_ID, filterValues, defaultValue);
  const options = [
    { value: "byName", title: t("nameString") },
    { value: "newestFirst", title: t("newestFirstString") },
    { value: "oldestFirst", title: t("oldestFirstString") },
  ];
  const selectedOption =
    options.find((option) => option.value === selected.value) ??
    options.find((option) => option.value === defaultValue.value) ??
    options[0];

  return {
    inputType: InputType.Select,
    filterType: FilterType.Sort,
    id: SORT_ID,
    title: t("sortByFilterTitleString", {
      option: selectedOption?.title ?? "-",
    }),
    options,
    enabled: true,
    defaultValue,
  };
}

function compareDate(item1: Group, item2: Group) {
  const date1 = new Date(item1.createdAt).getTime();
  const date2 = new Date(item2.createdAt).getTime();
  return date1 - date2;
}

function compareByName(item1: Group, item2: Group) {
  return item1.name.localeCompare(item2.name);
}

function useSortSelectGroupFilter(filterValues: FilterValue[]) {
  const filter: ApplyFilter<SelectFilter, Group> = {
    filter: useSortSelectFilter(filterValues),
    sortCompare: (item1: Group, item2: Group, { value }: FilterValue) => {
      switch (value) {
        case "newestFirst":
          return -compareDate(item1, item2);
        case "oldestFirst":
          return compareDate(item1, item2);
        case "byName":
        default:
          return compareByName(item1, item2);
      }
    },
  };

  return filter;
}

export { useSortSelectGroupFilter };
